import { Controller } from "@hotwired/stimulus"
import Logger from '../lib/logger'

export default class extends Controller {
  static values = {
    addressId: Number,
    latitude: Number,
    longitude: Number
  }

  connect() {
    // Controller ready for marker highlighting
    this.isHighlighting = false
    this.highlightTimeout = null
  }

  highlightMarker(event) {
    try {
      // Prevent multiple rapid highlights
      if (this.isHighlighting) return
      
      // Clear any pending highlight timeout
      if (this.highlightTimeout) {
        clearTimeout(this.highlightTimeout)
      }
      
      // Debounce highlighting for performance
      this.highlightTimeout = setTimeout(() => {
        this.performHighlight()
      }, 50) // Small delay to debounce rapid mouse movements
      
    } catch (error) {
      // Silent error handling
    }
  }

  getMapController() {
    try {
      const mapElement = document.querySelector('[data-controller*="map-leaflet"]')
      if (!mapElement) return null
      
      return this.application.getControllerForElementAndIdentifier(mapElement, 'map-leaflet')
    } catch (error) {
      return null
    }
  }

  performHighlight() {
    try {
      if (this.isHighlighting) return
      this.isHighlighting = true
      
      const mapController = this.getMapController()
      // Use public API isReady() instead of directly accessing internal properties
      if (!mapController || !mapController.isReady()) {
        this.isHighlighting = false
        return
      }

      // Find the marker using public API methods
      const marker = this.findMarkerByAddress(mapController)
      if (marker) {
        // Store original style for restoration
        this.originalMarkerStyle = this.getMarkerStyle(marker)
        
        // Apply drop effect with enhanced animation
        this.applyDropEffect(marker)
      }
      
      this.isHighlighting = false
    } catch (error) {
      this.isHighlighting = false
      // Silent error handling
    }
  }

  unhighlightMarker(event) {
    try {
      // Clear any pending highlight timeout
      if (this.highlightTimeout) {
        clearTimeout(this.highlightTimeout)
        this.highlightTimeout = null
      }
      
      // Reset highlighting flag
      this.isHighlighting = false
      
      // Restore marker to original state
      if (this.currentHighlightedMarker && this.originalMarkerStyle) {
        this.restoreMarkerStyle(this.currentHighlightedMarker)
        this.originalMarkerStyle = null
      }
    } catch (error) {
      // Silent error handling
    }
  }

  findMarkerByAddress(mapController) {
    try {
      // Use public API methods instead of directly accessing bids layer
      
      // Primary match: Try to find by address ID using public API
      if (this.hasAddressIdValue && this.addressIdValue) {
        const markerById = mapController.findMarkerByAddressId(this.addressIdValue)
        if (markerById) {
          return markerById
        }
      }
      
      // Secondary match: Try to find by coordinates using public API
      if (this.hasLatitudeValue && this.hasLongitudeValue &&
          this.latitudeValue && this.longitudeValue) {
        const markerByCoords = mapController.findMarkerByCoordinates(
          this.latitudeValue,
          this.longitudeValue,
          0.0001 // tolerance for floating point precision
        )
        if (markerByCoords) {
          return markerByCoords
        }
      }
      
      // Fallback: Search through all markers if public API methods don't find it
      // This handles cases where markers use addressData instead of addressId option
      const bidsLayer = mapController.getBidsLayer()
      if (!bidsLayer) return null

      let foundMarker = null
      
      bidsLayer.eachLayer(layer => {
        // Check if this layer is a marker (has a getLatLng method)
        if (!layer.getLatLng) return
        
        const addressData = layer.addressData
        
        if (addressData) {
          // Primary match: address ID (convert to string for comparison)
          const layerAddressId = String(addressData.id)
          const targetAddressId = String(this.addressIdValue)
          
          if (layerAddressId === targetAddressId) {
            foundMarker = layer
            return false // break the loop
          }
          
          // Secondary match: coordinates (with tolerance for floating point precision)
          const layerLat = parseFloat(addressData.latitude)
          const layerLng = parseFloat(addressData.longitude)
          const targetLat = parseFloat(this.latitudeValue)
          const targetLng = parseFloat(this.longitudeValue)
          
          if (!isNaN(layerLat) && !isNaN(layerLng) && !isNaN(targetLat) && !isNaN(targetLng)) {
            const latDiff = Math.abs(layerLat - targetLat)
            const lngDiff = Math.abs(layerLng - targetLng)
            
            if (latDiff < 0.0001 && lngDiff < 0.0001) {
              foundMarker = layer
              return false // break the loop
            }
          }
        }
      })

      return foundMarker
    } catch (error) {
      Logger.error('Error finding marker:', error)
      return null
    }
  }

  getMarkerStyle(marker) {
    try {
      // Store the current marker style for restoration
      const icon = marker.getIcon()
      const element = marker.getElement()
      
      if (!element) return null
      
      // Capture the current state including any Leaflet positioning
      const currentTransform = element.style.transform || ''
      
      return {
        iconUrl: icon.options.iconUrl,
        iconSize: icon.options.iconSize,
        originalTransform: currentTransform,
        originalFilter: element.style.filter || '',
        originalZIndex: element.style.zIndex || '',
        originalTransformOrigin: element.style.transformOrigin || '',
        originalTransition: element.style.transition || ''
      }
    } catch (error) {
      return null
    }
  }

  applyDropEffect(marker) {
    try {
      const element = marker.getElement()
      if (!element) return
      
      // Store the current marker for cleanup
      this.currentHighlightedMarker = marker
      
      // Get current theme for theme-aware highlighting
      const isDarkTheme = document.documentElement.getAttribute('data-bs-theme') === 'dark'
      
      // Parse the original Leaflet transform to preserve positioning
      const originalTransform = element.style.transform || ''
      
      // Extract translate3d values from Leaflet's positioning
      const translate3dMatch = originalTransform.match(/translate3d\(([^)]+)\)/)
      let baseTransform = ''
      
      if (translate3dMatch) {
        baseTransform = `translate3d(${translate3dMatch[1]})`
      } else {
        baseTransform = originalTransform
      }
      
      // Set up the marker for animation
      element.style.transformOrigin = 'center bottom'
      element.style.zIndex = '1000'
      element.style.willChange = 'transform, filter'
      
      // Create a comprehensive drop animation sequence
      this.animateDropSequence(element, baseTransform, isDarkTheme)
      
    } catch (error) {
      // Silent error handling to prevent disruption
    }
  }

  animateDropSequence(element, baseTransform, isDarkTheme) {
    // Phase 1: Initial lift (0.1s)
    element.style.transition = 'all 0.1s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
    element.style.transform = `${baseTransform} translateY(-12px) scale(1.1)`
    element.style.filter = 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2))'
    
    setTimeout(() => {
      // Phase 2: Drop down (0.3s) - main drop with gravity effect
      element.style.transition = 'all 0.3s cubic-bezier(0.55, 0.055, 0.675, 0.19)'
      element.style.transform = `${baseTransform} translateY(8px) scale(1.15)`
      element.style.filter = 'drop-shadow(0 12px 24px rgba(0, 0, 0, 0.4))'
      
      setTimeout(() => {
        // Phase 3: Bounce up (0.15s) - small bounce back
        element.style.transition = 'all 0.15s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
        element.style.transform = `${baseTransform} translateY(-2px) scale(1.12)`
        element.style.filter = 'drop-shadow(0 8px 16px rgba(0, 0, 0, 0.35))'
        
        setTimeout(() => {
          // Phase 4: Settle (0.2s) - final settle position
          element.style.transition = 'all 0.2s cubic-bezier(0.25, 1, 0.5, 1)'
          element.style.transform = `${baseTransform} translateY(0px) scale(1.1)`
          element.style.filter = 'drop-shadow(0 6px 12px rgba(0, 0, 0, 0.3))'
          
          setTimeout(() => {
            // Phase 5: Final highlight state (0.1s) - maintain elevated appearance
            element.style.transition = 'all 0.1s ease-out'
            element.style.transform = `${baseTransform} translateY(-3px) scale(1.08)`
            element.style.filter = 'drop-shadow(0 8px 16px rgba(0, 0, 0, 0.25))'
            
            // Clear will-change after animation completes
            setTimeout(() => {
              element.style.willChange = 'auto'
            }, 100)
            
          }, 200)
        }, 150)
      }, 300)
    }, 100)
  }


  restoreMarkerStyle(marker) {
    try {
      const element = marker.getElement()
      if (!element || !this.originalMarkerStyle) return
      
      // Ensure marker stays in map layer during restoration
      if (marker._map && marker._map.hasLayer && !marker._map.hasLayer(marker)) {
        marker.addTo(marker._map)
      }
      
      // Set up for smooth restoration animation
      element.style.willChange = 'transform, filter'
      element.style.transition = 'all 0.5s cubic-bezier(0.25, 1, 0.5, 1)'
      
      // Restore to original state (no drop effect, no scaling)
      element.style.filter = this.originalMarkerStyle.originalFilter
      element.style.transform = this.originalMarkerStyle.originalTransform
      element.style.zIndex = this.originalMarkerStyle.originalZIndex
      element.style.transformOrigin = this.originalMarkerStyle.originalTransformOrigin
      
      // Clear transition and animation after restoration
      setTimeout(() => {
        if (element && element.style) {
          element.style.transition = ''
          element.style.animation = ''
          element.style.willChange = 'auto'
        }
      }, 500)
      
      // Clear the highlighted marker reference
      this.currentHighlightedMarker = null
      
    } catch (error) {
      // Silent error handling
    }
  }


  disconnect() {
    // Clear any pending timeouts
    if (this.highlightTimeout) {
      clearTimeout(this.highlightTimeout)
      this.highlightTimeout = null
    }
    
    // Clean up any highlighted markers when controller is disconnected
    if (this.currentHighlightedMarker) {
      this.restoreMarkerStyle(this.currentHighlightedMarker)
    }
    
    // Reset state
    this.isHighlighting = false
    this.originalMarkerStyle = null
    this.currentHighlightedMarker = null
  }

}
